import { useTranslation } from 'react-i18next';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { BookOpen, Users, Calendar, ArrowRight, CheckCircle2, ChevronRight, GraduationCap, MapPin, Laptop, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function FutureStudents() {
  const { t } = useTranslation();

  const importantDates = [
    { event: 'Ouverture des candidatures', date: 'Janvier 2026', note: 'Dépôt des dossiers en ligne et au campus' },
    { event: 'Clôture des inscriptions', date: '30 Juin 2026', note: 'Délai de rigueur pour Bachelor et Master' },
    { event: 'Entretiens de sélection', date: 'Juillet — Août 2026', note: 'Test de niveau et entretien motivé' },
    { event: 'Rentrée Académique', date: 'Octobre 2026', note: 'Cérémonie solennelle au Palais des Congrès' }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('info_future_students', 'Futurs Étudiants')}
        description="Commencez votre parcours vers l'excellence académique et professionnelle. Découvrez nos programmes, nos conditions d'admission et préparez votre avenir."
        breadcrumbs={[
          { label: t('nav_admissions'), href: '/admissions' },
          { label: t('info_future_students'), href: '/admissions/future' }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
          
          {/* Main Content Grid */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            <div className="group bg-white border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden rounded-sm flex flex-col">
               <div className="h-48 overflow-hidden">
                 <img src="/images/campus/classroom-01.jpg" alt="Bachelor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
               </div>
               <div className="p-10 space-y-6 flex-1 flex flex-col">
                 <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-white transition-all duration-300">
                   <BookOpen className="w-6 h-6" />
                 </div>
                 <h3 className="text-2xl font-black text-navy uppercase tracking-tighter">{t('bachelor_title')}</h3>
                 <p className="text-gray-600 font-medium leading-relaxed">
                   Programmes de 3 ans (180 crédits) conçus pour former des techniciens et ingénieurs de haut niveau dans les métiers du futur.
                 </p>
                 <Link to="/programs/bachelor" className="mt-auto inline-flex items-center gap-2 text-navy font-black underline hover:text-gold uppercase tracking-widest text-xs transition-colors">
                    Voir les Filières <ChevronRight className="w-4 h-4" />
                 </Link>
               </div>
            </div>

            <div className="group bg-white border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden rounded-sm flex flex-col">
               <div className="h-48 overflow-hidden">
                 <img src="/images/campus/doc-gathering-01.jpg" alt="Master" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
               </div>
               <div className="p-10 space-y-6 flex-1 flex flex-col">
                 <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-white transition-all duration-300">
                   <Users className="w-6 h-6" />
                 </div>
                 <h3 className="text-2xl font-black text-navy uppercase tracking-tighter">{t('master_title')}</h3>
                 <p className="text-gray-600 font-medium leading-relaxed">
                   Spécialisations avancées de 2 ans (120 crédits) pour les professionnels et chercheurs en quête d'expertise pointue.
                 </p>
                 <Link to="/programs/master" className="mt-auto inline-flex items-center gap-2 text-navy font-black underline hover:text-gold uppercase tracking-widest text-xs transition-colors">
                    Voir les Filières <ChevronRight className="w-4 h-4" />
                 </Link>
               </div>
            </div>
          </section>

          {/* Admission Conditions Banner */}
          <section id="conditions" className="bg-gray-50 border-l-[12px] border-navy p-10 md:p-16 rounded-sm shadow-inner relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity">
              <ShieldCheck className="w-40 h-40 text-navy" />
            </div>
            <div className="relative z-10 space-y-8">
              <h2 className="text-3xl font-black text-navy uppercase tracking-tight italic">
                Conditions Générales d'Admission
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold" />
                    <span className="font-bold text-navy uppercase tracking-tighter">Baccalauréat ou GCE Advanced Level</span>
                  </div>
                  <p className="text-gray-500 text-sm font-medium pl-6">
                    Avoir validé au moins 3 matières au GCE A/L (hors Religious Knowledge) ou posséder un Baccalauréat d'enseignement général ou technique.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold" />
                    <span className="font-bold text-navy uppercase tracking-tighter">Formation tout au long de la vie</span>
                  </div>
                  <p className="text-gray-500 text-sm font-medium pl-6">
                    ESTAD encourage l'apprentissage continu. Aucune limite d'âge n'est imposée pour l'admission dans nos cycles professionnels.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Dates */}
          <section id="dates" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic leading-none">
              <Calendar className="w-8 h-8 text-gold" /> Calendrier de la Session 2026
            </h2>
            <div className="bg-white border border-gray-100 rounded-sm shadow-xl overflow-hidden divide-y divide-gray-100">
               {importantDates.map((item, idx) => (
                 <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between p-8 hover:bg-gray-50 transition-colors">
                   <div className="space-y-1">
                     <span className="text-xl font-black text-navy uppercase tracking-tighter">{item.event}</span>
                     <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{item.note}</p>
                   </div>
                   <div className="mt-4 md:mt-0 flex items-center gap-4 bg-navy text-white px-6 py-2 rounded-full">
                     <Calendar className="w-4 h-4 text-gold" />
                     <span className="font-black uppercase tracking-widest text-xs">{item.date}</span>
                   </div>
                 </div>
               ))}
            </div>
          </section>

          {/* Call to Action */}
          <section id="cta" className="bg-navy p-12 md:p-20 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-[2000ms] pointer-events-none"></div>
            <div className="relative z-10 text-center space-y-10">
               <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic italic leading-none">
                Prêt à rejoindre l'excellence ?
              </h2>
              <p className="text-white/60 text-lg font-medium max-w-2xl mx-auto leading-relaxed italic">
                "L'éducation est l'arme la plus puissante que vous puissiez utiliser pour changer le monde. ESTAD vous donne les outils pour construire ce changement."
              </p>
              <div className="flex flex-wrap justify-center gap-6 pt-4">
                 <Link to="/admissions" className="bg-gold text-navy px-16 py-5 font-black uppercase tracking-widest text-sm shadow-xl hover:brightness-110 transition-all active:scale-95 flex items-center gap-3">
                   Démarrer ma Candidature <ArrowRight className="w-5 h-5" />
                 </Link>
                 <Link to="/contact" className="border-2 border-white/20 text-white px-16 py-5 font-black uppercase tracking-widest text-sm hover:bg-white hover:text-navy transition-all active:scale-95">
                   Contacter un Campus
                 </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
